/**
 * TRAC-965 (second half, from TRAC-966) — what the agent did for the customer, told to Adobe
 * via the host page.
 *
 * The web team maps four response types to Adobe events 133–138. They used to ride an
 * `aiTireAgentResponse` push to the frame's own `window.dataLayer`, fired off a tool-completion
 * stream chunk. Both halves were dead: the current agent's stream has no tool lifecycle chunks,
 * and the frame's data layer never reaches the host page. This module sends them the way every
 * other moment leaves the frame (`hostAnalytics.ts`), keyed on the thing the customer actually
 * saw — the card — which is also what the Key Measures Engaged % counts (TRAC-966), so the web
 * team's numbers and our dashboard count the same thing.
 *
 *   card on the turn                              responseType         linkName
 *   tdg_link_card                                 tdg_link_provided    tr: ai tire agent: tdg link
 *   ymmv_interface completed (the picker pick)    fitment_lookup       tr: ai tire agent: fitment lookup
 *   source_cards (article / tire test / product)  tire_research_kb     tr: ai tire agent: tire research
 *   tire_browse_card                              category_search      tr: ai tire agent: category search
 *
 * Distinct link names per capability (Dylan, 2026-09-02): TRAC-968 sends one
 * `tr: ai tire agent: response` per answer, and these must never read as extra responses.
 * `responseType` still rides along so the web team's existing 133–138 mapping keys on it.
 *
 * Rules: once per capability per turn (two source-card sets on one turn fire once; a TDG
 * card and a browse card on one turn fire both); nothing on prompts (`selection_cards`, a
 * picker that was shown but not completed) or on prose-only turns; fired when the turn's
 * text has settled (`done`), not mid-stream. The picker pick is the customer's act, not a
 * stream card, so it fires on the commit itself.
 *
 * Nothing in here may throw or block (see the header of `services/handoffFunnel.ts`).
 */
import { postAnalyticsEvent, type AnalyticsEventMessage } from './hostAnalytics';

export type Capability =
  | 'tdg_link_provided'
  | 'fitment_lookup'
  | 'tire_research_kb'
  | 'category_search';

/** The Adobe custom-link names — final, verbatim (TRAC-965 second half, 2026-09-02). */
export const CAPABILITY_LINK_NAMES: Readonly<Record<Capability, string>> = Object.freeze({
  tdg_link_provided: 'tr: ai tire agent: tdg link',
  fitment_lookup: 'tr: ai tire agent: fitment lookup',
  tire_research_kb: 'tr: ai tire agent: tire research',
  category_search: 'tr: ai tire agent: category search',
});

export const CAPABILITY_EVENT = 'agent_capability';

/** Which capability a stream card announces, or null for prompts and everything else. */
export function capabilityForCard(chunkType: string | undefined | null): Capability | null {
  switch (chunkType) {
    case 'tdg_link_card': return 'tdg_link_provided';
    case 'source_cards': return 'tire_research_kb';
    case 'tire_browse_card': return 'category_search';
    default: return null; // selection_cards, ranking_cards, rating_display, navigation_card, ymmv prompts, …
  }
}

/** Post one capability to the host page. Null when not embedded or the post failed. */
export function postCapability(
  win: Window,
  capability: Capability,
  sessionId: string | null,
): AnalyticsEventMessage | null {
  return postAnalyticsEvent(win, {
    event: CAPABILITY_EVENT,
    linkName: CAPABILITY_LINK_NAMES[capability],
    responseType: capability,
  }, sessionId);
}

/**
 * The capabilities one agent turn has shown so far. `note` the card chunks as they stream,
 * `flush` once when the turn's text has settled — each capability posts once, in the order
 * first seen — and the set is empty again for the next turn.
 */
export class TurnCapabilities {
  private seen = new Set<Capability>();

  /** Record a stream chunk; returns the capability it announced, or null. */
  note(chunkType: string | undefined | null): Capability | null {
    const cap = capabilityForCard(chunkType);
    if (cap) this.seen.add(cap);
    return cap;
  }

  /** What has been noted for this turn, in first-seen order. */
  pending(): Capability[] {
    return [...this.seen];
  }

  /** Start a fresh turn without posting anything (a turn that failed, was retried, or was cleared). */
  reset(): void {
    this.seen.clear();
  }

  /** Post everything noted for this turn, once each, then reset. Never throws. */
  flush(win: Window, sessionId: string | null): AnalyticsEventMessage[] {
    const posted: AnalyticsEventMessage[] = [];
    try {
      for (const cap of this.seen) {
        const msg = postCapability(win, cap, sessionId);
        if (msg) posted.push(msg);
      }
    } catch {
      /* measurement never breaks the thing it measures */
    }
    this.seen.clear();
    return posted;
  }
}
